const CANCELLED_STATUSES = ['canceled', 'cancelled']
// Customer-cancelled paid subscriptions remain serviceable until ends_at.
// Cashier/Stripe data can represent that state as active or canceled/cancelled.
const PAID_THROUGH_STATUSES = ['active', 'canceled', 'cancelled']
const FAILED_PAYMENT_STATUSES = ['past_due', 'unpaid', 'incomplete', 'paused']

function getSubscriptionStatus(subscription) {
	if (!subscription) return null

	return subscription.stripe_status || subscription.status || null
}

function parseDate(value) {
	const date = new Date(value)
	return Number.isNaN(date.getTime()) ? null : date
}

export function hasFuturePaidThroughDate(subscription, now = new Date()) {
	if (!subscription?.ends_at || subscription.is_free) {
		return false
	}

	const endsAt = parseDate(subscription.ends_at)
	return !!endsAt && endsAt > now
}

export function isPaidThroughSubscription(subscription, now = new Date()) {
	const status = getSubscriptionStatus(subscription)

	return (
		PAID_THROUGH_STATUSES.includes(status) &&
		hasFuturePaidThroughDate(subscription, now)
	)
}

export function hasCurrentPaidSubscription(subscription, now = new Date()) {
	if (!subscription || subscription.is_free) {
		return false
	}

	const status = getSubscriptionStatus(subscription)

	if (status === 'active' && !subscription.ends_at) {
		return true
	}

	return isPaidThroughSubscription(subscription, now)
}

export function hasOrganizationPaidAccess(organization, now = new Date()) {
	if (!organization) {
		return false
	}

	if (typeof organization.has_paid_access === 'boolean') {
		return organization.has_paid_access
	}

	if (hasCurrentPaidSubscription(organization.subscription, now)) {
		return true
	}

	return (
		!Object.prototype.hasOwnProperty.call(
			organization,
			'has_paid_access'
		) && organization.is_paid === true
	)
}

export function isActivePaidSubscription(subscription) {
	return !!(
		subscription &&
		!subscription.is_free &&
		getSubscriptionStatus(subscription) === 'active' &&
		!subscription.ends_at
	)
}

export function didRenewToActiveSubscription(baseline, subscription) {
	return !!(
		baseline &&
		baseline.hadActivePaidSubscription === false &&
		isActivePaidSubscription(subscription)
	)
}

export function hasExpiredPaidSubscription(subscription, now = new Date()) {
	if (!subscription || hasCurrentPaidSubscription(subscription, now)) {
		return false
	}

	const status = getSubscriptionStatus(subscription)

	if (
		CANCELLED_STATUSES.includes(status) ||
		FAILED_PAYMENT_STATUSES.includes(status)
	) {
		return true
	}

	if (subscription.ends_at) {
		const endsAt = parseDate(subscription.ends_at)
		return !!endsAt && endsAt <= now
	}

	return false
}

export function hasFailedPaymentStatus(status) {
	return FAILED_PAYMENT_STATUSES.includes(status)
}

export function hasCancelledStatus(status) {
	return CANCELLED_STATUSES.includes(status)
}

export function getOrganizationAccessRecoveryPath(error) {
	if (error?.response?.status !== 402) {
		return null
	}

	const data = error.response?.data || {}
	if (data.trial_expired === true || data.payment_status === 'trial') {
		return '/organization/trial-expired'
	}

	if (data.payment_status === 'past_due') {
		return '/organization/payment-past-due'
	}

	if (data.payment_status === 'incomplete') {
		return '/organization/payment-incomplete'
	}

	if (data.payment_status === 'paused') {
		return '/organization/payment-paused'
	}

	return '/organization/subscription-expired'
}
